import React, { useState, useEffect } from 'react';
import api from '../services/api';

const InterviewHistory = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 640);
  
  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 640);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      console.log('🔍 Fetching interview history...');
      const response = await api.get('/api/ai-assistant/interview/history', {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('📊 Interview history response:', response.data);

      if (response.data.success) {
        setSessions(response.data.sessions || []);
        console.log(`✅ Loaded ${response.data.count} interview sessions`);
      } else {
        setError('Failed to load interview history');
      }
    } catch (error) {
      console.error('❌ Failed to fetch interview history:', error);
      if (error.response) {
        setError(`Server error: ${error.response.data.error || error.response.statusText}`);
      } else if (error.request) {
        setError('Network error: Unable to reach server');
      } else {
        setError('Error: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const viewSessionDetails = async (sessionId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/api/ai-assistant/interview/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSelectedSession(response.data);
      setShowDetails(true);
    } catch (error) {
      console.error('Failed to fetch session details:', error);
    }
  };

  const getRatingColor = (rating) => {
    if (!rating) return 'bg-gray-100 text-gray-700';
    if (rating.includes('Strong Hire') || rating === 'Pass') return 'bg-green-100 text-green-800';
    if (rating === 'Hire') return 'bg-blue-100 text-blue-800';
    if (rating.includes('No Hire') || rating === 'Fail') return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-700';
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toUpperCase()) {
      case 'EASY': return 'bg-green-100 text-green-700';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-700';
      case 'HARD': return 'bg-orange-100 text-orange-700';
      case 'EXPERT': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderMarkdownContent = (text) => {
    if (!text) return null;

    const lines = text.split('\n');
    const elements = [];
    let codeBlock = [];
    // eslint-disable-next-line no-unused-vars
    let _codeLanguage = ''; // Extracted but not currently used
    let inCodeBlock = false;
    let listItems = [];
    let inList = false;

    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(
          <ul key={`list-${elements.length}`} style={{
            margin: '12px 0',
            paddingLeft: '24px',
            listStyleType: 'disc'
          }}>
            {listItems.map((item, idx) => (
              <li key={idx} style={{
                margin: '6px 0',
                lineHeight: '1.7',
                color: '#374151'
              }}>{item}</li>
            ))}
          </ul>
        );
        listItems = [];
      }
    };

    lines.forEach((line, idx) => {
      // Handle code blocks
      if (line.startsWith('```')) {
        if (inCodeBlock) {
          // End code block
          elements.push(
            <pre key={`code-${idx}`} style={{
              margin: '16px 0',
              padding: '16px',
              background: '#1e1e1e',
              color: '#d4d4d4',
              borderRadius: '8px',
              overflow: 'auto',
              fontSize: '13px',
              lineHeight: '1.5',
              fontFamily: "'Fira Code', 'Consolas', 'Monaco', monospace",
              border: '1px solid #333'
            }}>
              <code>{codeBlock.join('\n')}</code>
            </pre>
          );
          codeBlock = [];
          inCodeBlock = false;
          _codeLanguage = '';
        } else {
          // Start code block
          flushList();
          inList = false;
          _codeLanguage = line.replace('```', '').trim();
          inCodeBlock = true;
        }
        return;
      }

      if (inCodeBlock) {
        codeBlock.push(line);
        return;
      }

      // Handle empty lines
      if (line.trim() === '') {
        flushList();
        inList = false;
        elements.push(<div key={`space-${idx}`} style={{ height: '8px' }} />);
        return;
      }

      // Handle headings
      if (line.startsWith('## ')) {
        flushList();
        inList = false;
        elements.push(
          <h3 key={`h3-${idx}`} style={{
            margin: '20px 0 12px 0',
            fontSize: '18px',
            fontWeight: '700',
            color: '#1f2937',
            borderBottom: '2px solid #e5e7eb',
            paddingBottom: '8px'
          }}>
            {line.replace('## ', '')}
          </h3>
        );
        return;
      }

      if (line.startsWith('# ')) {
        flushList();
        inList = false;
        elements.push(
          <h2 key={`h2-${idx}`} style={{
            margin: '24px 0 16px 0',
            fontSize: '22px',
            fontWeight: '700',
            color: '#111827',
            borderBottom: '3px solid #3b82f6',
            paddingBottom: '10px'
          }}>
            {line.replace('# ', '')}
          </h2>
        );
        return;
      }

      // Handle list items
      if (line.trim().startsWith('- ') || line.trim().startsWith('• ')) {
        inList = true;
        const itemText = line.trim().replace(/^[-•]\s*/, '');
        const formattedText = formatInlineMarkdown(itemText);
        listItems.push(formattedText);
        return;
      }

      // Handle numbered lists
      if (/^\d+\.\s/.test(line.trim())) {
        flushList();
        inList = false;
        const itemText = line.trim().replace(/^\d+\.\s*/, '');
        const formattedText = formatInlineMarkdown(itemText);
        elements.push(
          <div key={`num-${idx}`} style={{
            margin: '8px 0',
            paddingLeft: '0',
            lineHeight: '1.7',
            color: '#374151'
          }}>
            {line.trim().match(/^\d+\./)[0]} {formattedText}
          </div>
        );
        return;
      }

      // Handle normal paragraphs
      if (!inList) {
        flushList();
      }

      const formattedText = formatInlineMarkdown(line);
      
      if (!inList) {
        elements.push(
          <p key={`p-${idx}`} style={{
            margin: '8px 0',
            lineHeight: '1.7',
            color: '#374151',
            textAlign: 'left'
          }}>
            {formattedText}
          </p>
        );
      }
    });

    // Flush any remaining list items
    flushList();

    return <div style={{ textAlign: 'left' }}>{elements}</div>;
  };

  const formatInlineMarkdown = (text) => {
    const parts = text.split(/(\*\*\*.*?\*\*\*|\*\*.*?\*\*|`.*?`)/g);
    return parts.map((part, i) => {
      // Bold italic (***text***)
      if (part.startsWith('***') && part.endsWith('***')) {
        return (
          <strong key={i} style={{ fontWeight: '700', fontStyle: 'italic', color: '#1f2937' }}>
            {part.slice(3, -3)}
          </strong>
        );
      }
      // Bold (**text**)
      if (part.startsWith('**') && part.endsWith('**')) {
        return (
          <strong key={i} style={{ fontWeight: '700', color: '#1f2937' }}>
            {part.slice(2, -2)}
          </strong>
        );
      }
      // Inline code (`code`)
      if (part.startsWith('`') && part.endsWith('`')) {
        return (
          <code key={i} style={{
            background: '#f3f4f6',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '0.9em',
            fontFamily: "'Fira Code', 'Consolas', 'Monaco', monospace",
            color: '#e11d48',
            border: '1px solid #e5e7eb'
          }}>
            {part.slice(1, -1)}
          </code>
        );
      }
      return part;
    });
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: isMobile ? '24px 16px' : '40px' }}>
        <div style={{
          width: isMobile ? '32px' : '40px',
          height: isMobile ? '32px' : '40px',
          border: '4px solid #e5e7eb',
          borderTop: '4px solid #2563eb',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto'
        }} />
        <p style={{ marginTop: isMobile ? '12px' : '16px', color: '#6b7280', fontSize: isMobile ? '14px' : '16px' }}>Loading interview history...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        textAlign: 'center',
        padding: isMobile ? '32px 16px' : '60px 20px',
        background: '#fef2f2',
        borderRadius: isMobile ? '12px' : '12px',
        border: '2px solid #fecaca'
      }}>
        <div style={{ fontSize: isMobile ? '48px' : '64px', marginBottom: isMobile ? '12px' : '16px' }}>⚠️</div>
        <h3 style={{ margin: '0 0 8px 0', fontSize: isMobile ? '18px' : '20px', fontWeight: '600', color: '#991b1b' }}>
          Error Loading Interviews
        </h3>
        <p style={{ margin: '0 0 20px 0', fontSize: isMobile ? '13px' : '14px', color: '#dc2626', wordBreak: 'break-word' }}>
          {error}
        </p>
        <button
          onClick={fetchHistory}
          style={{
            padding: isMobile ? '12px 20px' : '10px 20px',
            background: '#2563eb',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: isMobile ? '14px' : '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s',
            minHeight: isMobile ? '44px' : 'auto'
          }}
          onMouseEnter={(e) => e.target.style.background = '#1d4ed8'}
          onMouseLeave={(e) => e.target.style.background = '#2563eb'}
        >
          🔄 Try Again
        </button>
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div style={{
        textAlign: 'center',
        padding: isMobile ? '32px 16px' : '60px 20px',
        background: '#f9fafb',
        borderRadius: '12px',
        border: '2px dashed #d1d5db'
      }}>
        <div style={{ fontSize: isMobile ? '48px' : '64px', marginBottom: isMobile ? '12px' : '16px' }}>🎤</div>
        <h3 style={{ margin: '0 0 8px 0', fontSize: isMobile ? '18px' : '20px', fontWeight: '600', color: '#1f2937' }}>
          No Interview History Yet
        </h3>
        <p style={{ margin: '0 0 20px 0', fontSize: isMobile ? '13px' : '14px', color: '#6b7280' }}>
          Complete your first AI interview to see your progress here
        </p>
        <p style={{ margin: 0, fontSize: isMobile ? '12px' : '13px', color: '#9ca3af', fontStyle: 'italic' }}>
          💡 Tip: Make sure to submit and get evaluation for the interview to be saved
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Header with refresh button */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'center',
        gap: isMobile ? '12px' : 0,
        marginBottom: isMobile ? '16px' : '20px'
      }}>
        <div style={{ color: '#6b7280', fontSize: isMobile ? '13px' : '14px' }}>
          {sessions.length} interview{sessions.length !== 1 ? 's' : ''} completed
        </div>
        <button
          onClick={fetchHistory}
          disabled={loading}
          style={{
            padding: isMobile ? '10px 16px' : '8px 16px',
            background: 'white',
            color: '#2563eb',
            border: '1px solid #2563eb',
            borderRadius: '6px',
            fontSize: isMobile ? '14px' : '13px',
            fontWeight: '600',
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            minHeight: isMobile ? '44px' : 'auto',
            width: isMobile ? '100%' : 'auto',
            justifyContent: isMobile ? 'center' : 'flex-start'
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.target.style.background = '#eff6ff';
            }
          }}
          onMouseLeave={(e) => {
            if (!loading) {
              e.target.style.background = 'white';
            }
          }}
        >
          <span>🔄</span>
          <span>Refresh</span>
        </button>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: isMobile ? '16px' : '20px'
      }}>
        {sessions.map((session) => (
          <div
            key={session.id}
            style={{
              background: 'white',
              borderRadius: isMobile ? '12px' : '12px',
              padding: isMobile ? '16px' : '20px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              cursor: 'pointer',
              transition: 'all 0.2s',
              minHeight: isMobile ? 'auto' : 'auto'
            }}
            onClick={() => viewSessionDetails(session.id)}
            onMouseEnter={(e) => {
              if (!isMobile) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isMobile) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
              }
            }}
          >
            {/* Header */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'flex-start', 
              marginBottom: isMobile ? '10px' : '12px',
              flexWrap: 'wrap',
              gap: isMobile ? '8px' : 0
            }}>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getDifficultyColor(session.difficulty)}`} style={{ fontSize: isMobile ? '11px' : '12px' }}>
                {session.difficulty}
              </span>
              {session.overallRating && (
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getRatingColor(session.overallRating)}`} style={{ fontSize: isMobile ? '11px' : '12px' }}>
                  {session.overallRating}
                </span>
              )}
            </div>

            {/* Focus Area */}
            <h4 style={{ 
              margin: '0 0 8px 0', 
              fontSize: isMobile ? '15px' : '16px', 
              fontWeight: '600', 
              color: '#1f2937',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: '1.4'
            }}>
              {session.focusArea}
            </h4>

            {/* Problem Preview */}
            <p style={{
              margin: '0 0 12px 0',
              fontSize: isMobile ? '12px' : '13px',
              color: '#6b7280',
              lineHeight: '1.5',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}>
              {session.problemPreview}
            </p>

            {/* Footer */}
            <div style={{ 
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between', 
              alignItems: isMobile ? 'flex-start' : 'center', 
              fontSize: isMobile ? '11px' : '12px', 
              color: '#9ca3af',
              gap: isMobile ? '6px' : 0
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '8px', flexWrap: 'wrap' }}>
                <span>💻 {session.language}</span>
                {session.durationMinutes && (
                  <span>• ⏱️ {session.durationMinutes}m</span>
                )}
              </div>
              <span style={{ fontSize: isMobile ? '11px' : '12px' }}>{formatDate(session.completedAt)}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Details Modal */}
      {showDetails && selectedSession && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: isMobile ? 'flex-end' : 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: isMobile ? '0' : '20px'
          }}
          onClick={() => setShowDetails(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: isMobile ? '20px 20px 0 0' : '16px',
              maxWidth: '900px',
              width: '100%',
              maxHeight: isMobile ? '95vh' : '90vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: isMobile ? '16px' : '24px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: isMobile ? '12px' : 0
            }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: '0 0 8px 0', fontSize: isMobile ? '20px' : '24px', fontWeight: '600', color: '#1f2937' }}>
                  Interview Details
                </h3>
                <div style={{ display: 'flex', gap: isMobile ? '6px' : '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getDifficultyColor(selectedSession.difficulty)}`} style={{ fontSize: isMobile ? '11px' : '12px' }}>
                    {selectedSession.difficulty}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getRatingColor(selectedSession.overallRating)}`} style={{ fontSize: isMobile ? '11px' : '12px' }}>
                    {selectedSession.overallRating}
                  </span>
                  <span style={{ fontSize: isMobile ? '11px' : '12px', color: '#6b7280' }}>
                    💻 {selectedSession.language}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setShowDetails(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: isMobile ? '24px' : '28px',
                  color: '#9ca3af',
                  cursor: 'pointer',
                  padding: '0',
                  width: isMobile ? '36px' : '32px',
                  height: isMobile ? '36px' : '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '6px',
                  transition: 'all 0.2s',
                  minWidth: isMobile ? '36px' : '32px',
                  flexShrink: 0
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#f3f4f6';
                  e.target.style.color = '#1f2937';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#9ca3af';
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div style={{ 
              flex: 1, 
              overflowY: 'auto', 
              padding: isMobile ? '16px' : '24px',
              WebkitOverflowScrolling: 'touch'
            }}>
              {/* Focus Area */}
              <div style={{ marginBottom: isMobile ? '20px' : '24px' }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: isMobile ? '12px' : '14px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Focus Area
                </h4>
                <p style={{ margin: 0, fontSize: isMobile ? '15px' : '16px', color: '#1f2937', fontWeight: '500', wordBreak: 'break-word' }}>
                  {selectedSession.focusArea}
                </p>
              </div>

              {/* Problem Statement */}
              <div style={{ marginBottom: isMobile ? '20px' : '24px' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: isMobile ? '12px' : '14px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Problem Statement
                </h4>
                <div style={{
                  padding: isMobile ? '16px' : '20px',
                  background: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  overflowX: 'auto'
                }}>
                  {renderMarkdownContent(selectedSession.problemStatement)}
                </div>
              </div>

              {/* Final Code */}
              <div style={{ marginBottom: isMobile ? '20px' : '24px' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: isMobile ? '12px' : '14px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Your Solution
                </h4>
                <div style={{ textAlign: 'left' }}>
                  {selectedSession.finalCode && !selectedSession.finalCode.includes('```') ? (
                    <pre style={{
                      margin: '0',
                      padding: isMobile ? '12px' : '16px',
                      background: '#1e1e1e',
                      color: '#d4d4d4',
                      borderRadius: '8px',
                      overflow: 'auto',
                      fontSize: isMobile ? '11px' : '13px',
                      lineHeight: '1.5',
                      fontFamily: "'Fira Code', 'Consolas', 'Monaco', monospace",
                      border: '1px solid #333',
                      textAlign: 'left',
                      wordBreak: 'break-word',
                      whiteSpace: 'pre-wrap'
                    }}>
                      {selectedSession.finalCode}
                    </pre>
                  ) : (
                    renderMarkdownContent(selectedSession.finalCode)
                  )}
                </div>
              </div>

              {/* Evaluation */}
              <div>
                <h4 style={{ margin: '0 0 12px 0', fontSize: isMobile ? '12px' : '14px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Evaluation & Feedback
                </h4>
                <div style={{
                  padding: isMobile ? '16px' : '20px',
                  background: '#eff6ff',
                  borderRadius: '8px',
                  border: '1px solid #bfdbfe',
                  overflowX: 'auto'
                }}>
                  {renderMarkdownContent(selectedSession.evaluation)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InterviewHistory;

